import { useEffect, useMemo, useReducer, useRef, useState } from "react";

import { Authorization, AuthStatus, UnitUsageRange } from "@asd/customer-portal-api";
import { formatDate, toWallClockDate } from "@asd/customer-portal-ui";
import CheckIcon from "@mui/icons-material/Check";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import PauseIcon from "@mui/icons-material/Pause";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Checkbox,
  Chip,
  darken,
  Divider,
  FormControlLabel,
  lighten,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel, useGridApiRef } from "@mui/x-data-grid";

import { FilterAutocomplete, FilterTextField } from "~/components/shared";
import { getScrambledDateString, useScrambler } from "~/providers/ScramblerProvider";
import { BudgetService } from "~/services";
import { USDollar } from "~/utils/stringConverters";
import { useTableExport } from "~/utils/tableExport";
import { isAdditionalEmployerExpense, UNIT_USAGE_OPTIONS } from "~/utils/unitUsageHelpers";
import { useDashboardStaffProvider } from "../dashboard_staff/DashboardStaffProvider";

const PERCENTAGE_CHIP_STYLE = {
  color: "white",
  textShadow: "1px 1px 0 black, -1px -1px 0 black, 1px -1px 0 black, -1px 1px 0 black",
};

const dollarRenderer = (params: GridRenderCellParams<any, number>) => {
  if (isAdditionalEmployerExpense(params.row.description)) {
    return params.value ? USDollar.format(params.value) : null;
  }

  return params.value;
};

const columns: GridColDef<Authorization>[] = [
  {
    field: "authReference",
    headerName: "#",
    width: 70,
  },
  {
    field: "startDate",
    headerName: "Start Date",
    minWidth: 100,
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDate,
  },
  {
    field: "stopDate",
    headerName: "End Date",
    minWidth: 100,
    flex: 1,
    type: "date",
    valueGetter: toWallClockDate,
    valueFormatter: formatDate,
  },
  {
    field: "description",
    headerName: "Description",
    minWidth: 100,
    flex: 2,
  },
  {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    type: "singleSelect",
    valueOptions: Object.entries(AuthStatus),
    renderCell: (params: GridRenderCellParams<any, string>) => {
      let color: "default" | "success" | "warning" | "primary" = "default",
        icon: React.ReactNode,
        variant: "filled" | "outlined" = "filled";
      if (params.value === "Active") {
        color = "success";
        icon = <CheckIcon />;
      } else if (params.value === "Inactive") {
        color = "warning";
        icon = <WarningIcon />;
      } else {
        color = "primary";
        icon = <PauseIcon />;
        variant = "outlined";
      }

      return <Chip color={color} icon={icon} label={params.value} size="small" variant={variant} />;
    },
  },
  {
    field: "paymentCount",
    headerName: "# of Payments",
    minWidth: 100,
    type: "number",
  },
  {
    field: "unitUsagePct",
    headerName: "Unit Usage",
    minWidth: 100,
    flex: 1,
    type: "number",
    renderCell: (params: GridRenderCellParams<any, number>) => {
      const usedPercentage = params.value ?? 0;
      const unitUsageOption = UNIT_USAGE_OPTIONS.find(({ id }) => id === params.row.unitUsageRange);
      const backgroundColor = (unitUsageOption || UNIT_USAGE_OPTIONS.at(-1))?.backgroundColor;

      return (
        <Box sx={{ display: "flex", alignItems: "center", position: "relative", height: "100%" }}>
          <Tooltip title={`${usedPercentage?.toFixed(2)}%`}>
            <Stack
              direction="column"
              sx={(theme) => ({
                width: "100%",
                height: "100%",
                borderRadius: theme.shape.borderRadius,
                border: `1px solid ${theme.palette.divider}`,
              })}
            >
              <Stack
                sx={{
                  width: `${usedPercentage}%`,
                  maxWidth: "100%",
                  height: "100%",
                  backgroundColor,
                  borderRadius: (theme) => theme.shape.borderRadius,
                  alignItems: "start",
                  justifyContent: "center",
                }}
              >
                <Typography pl={1} sx={PERCENTAGE_CHIP_STYLE}>
                  {`${Math.floor(usedPercentage)}%`}
                </Typography>
              </Stack>
            </Stack>
          </Tooltip>
        </Box>
      );
    },
  },
  {
    field: "unitsAllocated",
    headerName: "Total Approved Units",
    type: "number",
    flex: 1,
    renderCell: dollarRenderer,
  },
  {
    field: "unitsSpent",
    headerName: "Units Used",
    type: "number",
    flex: 1,
    renderCell: dollarRenderer,
  },
  {
    field: "unitsRemaining",
    headerName: "Units Remaining",
    type: "number",
    flex: 1,
    renderCell: dollarRenderer,
  },
];

const Authorizations = ({ setSelectedAuthorizations }: { setSelectedAuthorizations: (_authorization: Authorization[]) => void }) => {
  const theme = useTheme();
  const tableRef = useGridApiRef();
  const exportTable = useTableExport();

  const { isScramblerEnabled } = useScrambler();
  const { selectedParticipant } = useDashboardStaffProvider();

  const [isLoading, setIsLoading] = useState(false);
  const [hideFilters, setHideFilters] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  });

  const authorizationsAbortController = useRef<AbortController>(new AbortController());
  const authorizationsExportAbortController = useRef<AbortController>(new AbortController());
  const previousDataSourceState = useRef({ rows: [], rowCount: 0 });

  const [tableFilters, setTableFilters] = useReducer(
    (state, action) => ({
      ...state,
      [action.key]: action.value,
    }),
    {
      showInactive: false,
      description: "",
      unitUsage: [],
    },
  );

  const dataSource = useMemo(
    () => ({
      getRows: async (params: any) => {
        authorizationsAbortController.current?.abort();
        authorizationsAbortController.current = new AbortController();

        if (!tableFilters.unitUsage.length) {
          return {
            rows: [],
            rowCount: 0,
          };
        }

        setIsLoading(true);

        try {
          const authorizations = await BudgetService.getAuthorizations(
            selectedParticipant?.participantId,
            tableFilters.description,
            tableFilters.showInactive ? undefined : [AuthStatus.Active, AuthStatus.Pending],
            tableFilters.unitUsage.map(({ id }) => id),
            params.paginationModel.page,
            params.paginationModel.pageSize,
            params.sortModel.map(({ field, sort }: { field: string; sort: string }) => `${field}:${sort}`),
            {
              signal: authorizationsAbortController.current?.signal,
            },
          );

          if (BudgetService.getPendingRequestCount("auths") === 0) {
            if (isScramblerEnabled) {
              (authorizations?.data?.items ?? []).forEach((authorization: Authorization, index: number) => {
                authorization.authReference = index + 1;
                authorization.startDate = getScrambledDateString(authorization.startDate);
                authorization.stopDate = getScrambledDateString(authorization.stopDate);
              });
            }

            setIsLoading(false);
            previousDataSourceState.current = {
              rows: authorizations?.data?.items ?? [],
              rowCount: authorizations?.data?.total,
            };
          }

          return previousDataSourceState.current;
        } catch (error: any) {
          if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
            setIsLoading(false);
          }
          throw error;
        }
      },
    }),
    [tableFilters, isScramblerEnabled, selectedParticipant?.participantId],
  );

  useEffect(
    () => () => {
      authorizationsAbortController.current?.abort();
      authorizationsAbortController.current = new AbortController();
      authorizationsExportAbortController.current?.abort();
      authorizationsExportAbortController.current = new AbortController();
    },
    [],
  );

  useEffect(() => {
    const selectedRowIds = Array.from(rowSelectionModel.ids).map((id) => `${id}`);
    setSelectedAuthorizations(
      previousDataSourceState.current.rows.filter(({ authorizationServiceId }) => selectedRowIds.includes(authorizationServiceId ?? "")),
    );

    return tableRef.current?.subscribeEvent("headerSelectionCheckboxChange", () => {
      if (selectedRowIds.length) {
        setRowSelectionModel({ type: "include", ids: new Set() });
      }
    });
  }, [rowSelectionModel.ids, setSelectedAuthorizations, tableRef]);

  return (
    <Box sx={{ p: 1, height: "100%", justifyContent: "center", alignItems: "center", overflow: "auto" }}>
      <Paper elevation={1} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Stack alignItems="start" direction="row" flexWrap="wrap" gap={1} overflow="auto" p={1}>
          <FormControlLabel
            control={
              <Switch
                checked={hideFilters}
                onChange={() => {
                  setHideFilters((prev) => !prev);
                }}
                size="small"
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography>Hide Filters</Typography>}
            sx={{ minHeight: "2em" }}
          />
          <Divider orientation="vertical" sx={[hideFilters && { display: "none" }]} flexItem />
          <FormControlLabel
            control={
              <Checkbox
                checked={tableFilters.showInactive}
                onChange={(event) =>
                  setTableFilters({
                    key: "showInactive",
                    value: event.target.checked,
                  })
                }
                size="small"
                sx={{ p: 0.5 }}
              />
            }
            label={<Typography>Show Inactive</Typography>}
            sx={[{ minHeight: "2em" }, hideFilters && { display: "none" }]}
          />
          <Divider orientation="vertical" sx={[hideFilters && { display: "none" }]} flexItem />
          <FilterTextField
            filterKey="description"
            filters={tableFilters}
            label="Search in Description"
            setFilters={setTableFilters}
            sx={{
              flexGrow: 1,
              width: 0,
              minWidth: "10rem",
              ...(hideFilters && { display: "none" }),
            }}
          />
          <Divider orientation="vertical" sx={[hideFilters && { display: "none" }]} flexItem />
          <FilterAutocomplete
            filterKey="unitUsage"
            filters={tableFilters}
            getChipStyle={(option) => ({
              "&.MuiAutocomplete-tag": {
                height: "100%",
                my: 0,
                backgroundColor: option.backgroundColor,
                ...PERCENTAGE_CHIP_STYLE,

                "& .MuiChip-deleteIcon": {
                  color: darken(theme.palette.common.white, 0.2),
                },
              },
            })}
            label="Filter by Unit Usage"
            onChange={(_: any, newValue: Array<{ id: string }>) => {
              const counts = new Map();

              newValue.forEach(({ id }) => {
                counts.set(id, (counts.get(id) || 0) + 1);
              });

              setTableFilters({
                key: "unitUsage",
                value: newValue.filter(({ id }) => counts.get(id) === 1) ?? [],
              });
            }}
            options={UNIT_USAGE_OPTIONS}
            renderOption={(props: any, option: any, _: any, ownerState: any) => {
              const { key, ...optionProps } = props;
              return (
                <Box key={key} component="li" {...optionProps} width="100%">
                  <Checkbox
                    checked={tableFilters.unitUsage.some(({ id }) => id === option.id)}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    style={{ marginRight: 8 }}
                    sx={{
                      p: 0.5,
                    }}
                  />
                  <Stack
                    sx={{
                      width: `${Math.min(option.maxValue ?? option.minValue ?? 0, 100)}%`,
                      maxWidth: "100%",
                      height: "100%",
                      backgroundColor: option.backgroundColor,
                      borderRadius: (theme) => theme.shape.borderRadius,
                      alignItems: "start",
                      justifyContent: "center",
                    }}
                  >
                    <Typography px={1} sx={PERCENTAGE_CHIP_STYLE}>
                      {ownerState.getOptionLabel(option)}
                    </Typography>
                  </Stack>
                </Box>
              );
            }}
            setFilters={setTableFilters}
            sx={{
              flexGrow: 0,

              ...(hideFilters && { display: "none" }),
            }}
            valueSort={(a: any, b: any) =>
              UNIT_USAGE_OPTIONS.findIndex((option) => option.id === a.id) - UNIT_USAGE_OPTIONS.findIndex((option) => option.id === b.id)
            }
          />
        </Stack>
        <DataGrid
          apiRef={tableRef}
          columns={columns}
          dataSource={dataSource}
          getRowClassName={({ row, indexRelativeToCurrentPage }) => {
            let className = indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";

            if (row.unitUsageRange === UnitUsageRange.Over100) {
              className = "overBudget";
            }

            return className;
          }}
          getRowId={({ authorizationServiceId }) => authorizationServiceId ?? ""}
          loading={isLoading}
          onRowSelectionModelChange={(params, { reason }) => {
            const adjustedParams = { ...params };

            if (params.ids.size > 1 && rowSelectionModel.ids.size === 1) {
              const idToDelete = [...adjustedParams.ids].find((id) => id === rowSelectionModel.ids.values().next().value);
              if (idToDelete) {
                adjustedParams.ids.delete(idToDelete);
              }
            }

            if (reason === "multipleRowsSelection" || adjustedParams.ids.size < 2) {
              setRowSelectionModel(adjustedParams);
            }
          }}
          rowSelectionModel={rowSelectionModel}
          slotProps={{
            toolbar: {
              label: "Services",
              onExport: async (format) => {
                authorizationsExportAbortController.current?.abort();
                authorizationsExportAbortController.current = new AbortController();

                const data = await BudgetService.exportAuthorizations(
                  selectedParticipant?.participantId,
                  tableFilters.description,
                  tableFilters.showInactive ? undefined : [AuthStatus.Active, AuthStatus.Pending],
                  tableFilters.unitUsage.map(({ id }) => id),
                  (tableRef.current?.getSortModel() ?? []).map(({ field, sort }) => `${field}:${sort ?? "asc"}`),
                  {
                    signal: authorizationsExportAbortController.current?.signal,
                  },
                );

                exportTable(data, "Services", format);
              },
            },
          }}
          slots={{
            ...(!tableFilters.unitUsage.length && {
              noRowsOverlay: () => (
                <Typography
                  sx={{
                    justifySelf: "center",
                    height: "100%",
                    alignContent: "center",
                  }}
                >
                  Please select at least one Unit Usage
                </Typography>
              ),
            }),
          }}
          sx={(theme) => {
            const modifier = theme.palette.mode === "light" ? lighten : darken;

            return {
              ...(isLoading && {
                ".MuiDataGrid-columnHeaders": {
                  pointerEvents: "none",
                },
              }),
              "& .MuiDataGrid-columnHeaderTitle": {
                color: theme.palette.custom.main,
              },
              "& .MuiDataGrid-toolbar .MuiDataGrid-columnHeaderTitle": {
                ...theme.typography.h5,
                fontWeight: theme.typography.fontWeightBold,
              },
              "& .overBudget": (theme) => ({
                backgroundColor: modifier(theme.palette.error.main, 0.5),
                "&.Mui-selected": {
                  backgroundColor: modifier(theme.palette.error.main, 0.2),
                  "&:hover": {
                    backgroundColor: modifier(theme.palette.error.main, 0.7),
                  },
                },
                "&:hover": {
                  backgroundColor: modifier(theme.palette.error.main, 0.7),
                },
              }),
            };
          }}
          checkboxSelection
        />
      </Paper>
    </Box>
  );
};

export default Authorizations;
